/**
 * La píldora «Migrando · NN %» de la barra superior (spec docs/superpowers/specs/2026-10-20-asistente-migracion-design.md
 * §4.2 y §5.10; plan docs/superpowers/plans/2026-10-20-asistente-migracion-e1.md, Task 12).
 *
 * El navbar (application/views/noble/layout/navbar.php) la pinta ya con su porcentaje mientras la corrida va en curso o
 * terminó sin que se viera el paso 5 (Migracion_asistente::barra()), y noble/layout/_fd_migracion.php la monta aquí con
 * window.FDMigracion.iniciarPildora. Mientras migra pregunta a v2/migracion/estado_ajax con pildora=1 (solo porcentaje,
 * fase y corrida; el servidor suelta la sesión) cada intervaloSondeo() con la pestaña a la vista. Terminada ya no
 * pregunta: lo que sigue (elegir productos, ver el paso 5) pasa en el asistente, que es adonde lleva un clic.
 *
 * El último estado se comparte entre pestañas en localStorage (fd.migracion.<emisorId>, siempre con try/catch): ninguna
 * pinta un porcentaje menor que el que otra ya vio, una pestaña no pregunta si otra lo acaba de hacer (y lo que esa trae
 * se pinta aquí por el evento storage), y el aviso «Tu cuenta está lista» sale una sola vez. Sin localStorage todo sigue
 * igual, salvo que el aviso solo sale si esta pestaña ve terminar la migración.
 *
 * Nada renueva la sesión sin la persona (FD.sesion): tras 10 minutos sin interacción (pointerdown, keydown, foco de la
 * ventana o volver a la pestaña; reglas.sondeoVigente) deja de preguntar y la siguiente interacción la reanuda. Si la
 * sesión terminó o el token cambió (401 o 419: FD.sesion ya se encarga), se pausa hasta 'fd:sesion-reanudada'; sin el
 * permiso (403), se detiene.
 *
 * Lo puro se prueba en node (src/js/migracion/__tests__/pildora.test.js); el montaje recibe la ventana en `ventana` para
 * probarlo con una de mentiras. Todo se pinta con textContent.
 */
import { intervaloSondeo, porcentajeVisible, sondeoVigente } from './reglas.js';

var PREFIJO_CLAVE = 'fd.migracion.';
var FASES = { migrando: true, productos: true, listo: true };
/** Lo que quedó pendiente al terminar (R40, el mismo criterio del paso 5): meses de CFDIs sin traer o un catálogo. */
var PENDIENTES = { meses: true, catalogos: true };
var AVISOS = {
  meses: { tipo: 'info', texto: 'Tu cuenta está casi lista: algunos meses quedaron pendientes y puedes volver a intentarlo desde el asistente.' },
  catalogos: { tipo: 'info', texto: 'Tu cuenta está casi lista: una parte de tus catálogos quedó pendiente; escríbenos a soporte@facturadigital.com.mx y la terminamos contigo.' },
};

/** La llave de localStorage de la cuenta. */
export function claveAlmacen(emisorId) {
  return PREFIJO_CLAVE + String(emisorId);
}

function faseValida(f) {
  return typeof f === 'string' && Object.prototype.hasOwnProperty.call(FASES, f) ? f : null;
}

/**
 * {id, porcentaje, fase, t, avisado} con lo que haya (fase null si no es una conocida), o null si no es un objeto. Si la
 * corrida terminó con algo pendiente (R40: 'meses' | 'catalogos', lo que manda Migracion_asistente::pildora()), también
 * `pendiente`; cualquier otro valor no cuenta.
 */
export function normalizarEstado(e) {
  if (!e || typeof e !== 'object') return null;
  var t = Number(e.t);
  var id = (e.id === null || e.id === undefined || e.id === '') ? null : String(e.id);
  var r = {
    id: id,
    porcentaje: porcentajeVisible(e.porcentaje, 0),
    fase: faseValida(e.fase),
    t: isFinite(t) && t > 0 ? t : 0,
    avisado: e.avisado === true,
  };
  if (typeof e.pendiente === 'string' && Object.prototype.hasOwnProperty.call(PENDIENTES, e.pendiente)) r.pendiente = e.pendiente;
  return r;
}

/**
 * Lo que queda de dos estados (el guardado por otra pestaña y el nuevo): el porcentaje mayor, la fecha más reciente y la
 * fase de quien la trae (o la del otro si esa no trae). Si son de corridas distintas (Premium que vuelve por años
 * anteriores) manda el más reciente entero: el 100 % de la corrida anterior ya no vale. Lo mismo si la MISMA corrida
 * vuelve a migrar después de terminar («Volver a intentar», ola final A1): su porcentaje se vuelve a calcular y el aviso
 * «Tu cuenta está lista» vuelve a salir cuando termine. (Una respuesta vieja de «migrando» que llegue justo después de que
 * otra pestaña vio terminar se corrige en el siguiente sondeo; a lo más, el aviso sale dos veces.)
 */
export function fusionarEstado(guardado, nuevo) {
  var g = normalizarEstado(guardado);
  var n = normalizarEstado(nuevo);
  if (!g || !n) return g || n;
  var reciente = n.t >= g.t ? n : g;
  var otro = reciente === n ? g : n;
  if (g.id !== null && n.id !== null && g.id !== n.id) return reciente;
  if (reciente.fase === 'migrando' && otro.fase !== null && otro.fase !== 'migrando') {
    return { id: reciente.id !== null ? reciente.id : otro.id, porcentaje: reciente.porcentaje, fase: 'migrando', t: reciente.t, avisado: false };
  }
  var r = {
    id: reciente.id !== null ? reciente.id : otro.id,
    porcentaje: Math.max(g.porcentaje, n.porcentaje),
    fase: reciente.fase || otro.fase,
    t: reciente.t,
    avisado: g.avisado || n.avisado,
  };
  var pendiente = reciente.pendiente || otro.pendiente;
  if (pendiente) r.pendiente = pendiente;
  return r;
}

/**
 * El aviso al terminar (R40): «Tu cuenta está lista» solo sin pendientes; con meses de CFDIs sin traer, la variante serena
 * con la invitación a volver a intentar desde el asistente; con un catálogo pendiente, soporte. Lo «no disponible en el
 * SAT» no es pendiente (R36): el servidor no lo manda como tal. {tipo para FD.toast, texto}.
 */
export function avisoListo(estado) {
  var s = normalizarEstado(estado);
  if (s && s.pendiente && AVISOS[s.pendiente]) return { tipo: AVISOS[s.pendiente].tipo, texto: AVISOS[s.pendiente].texto };
  return { tipo: 'success', texto: 'Tu cuenta está lista' };
}

/**
 * Lo que dice la píldora: {titulo, pct, etiqueta} (el mismo texto que pinta el navbar), o null sin una fase conocida.
 * Terminada con algo pendiente (R42, como el aviso de R40), «Casi lista».
 */
export function textoPildora(e) {
  var s = normalizarEstado(e);
  if (!s || !s.fase) return null;
  if (s.fase === 'listo' && s.pendiente) return { titulo: 'Casi lista', pct: '', etiqueta: 'Asistente de migración: tu cuenta está casi lista' };
  if (s.fase === 'listo') return { titulo: 'Lista', pct: '', etiqueta: 'Asistente de migración: tu cuenta está lista' };
  if (s.fase === 'productos') return { titulo: 'Elige tus productos', pct: '', etiqueta: 'Asistente de migración: elige tus productos' };
  return { titulo: 'Migrando', pct: s.porcentaje + ' %', etiqueta: 'Asistente de migración: migrando, ' + s.porcentaje + ' %' };
}

/**
 * ¿Sale el aviso «Tu cuenta está lista»? La primera vez que se ve la corrida en «listo» (avisado viaja en localStorage:
 * otra pestaña que ya avisó lo apaga aquí). Sin localStorage (conMemoria false), solo al verla pasar a «listo» en esta
 * pestaña: si no, saldría en cada pantalla mientras no se vea el paso 5.
 */
export function debeAvisarListo(previo, actual, conMemoria) {
  var a = normalizarEstado(actual);
  if (!a || a.fase !== 'listo' || a.avisado) return false;
  if (conMemoria) return true;
  var p = normalizarEstado(previo);
  return !!p && p.fase !== null && p.fase !== 'listo' && (p.id === null || a.id === null || p.id === a.id);
}

/** Cada cuánto vuelve a preguntar (ms), o null: solo mientras migra y con la pestaña a la vista. */
export function proximoSondeo(estado, visible) {
  var s = normalizarEstado(estado);
  if (!s || s.fase !== 'migrando') return null;
  return intervaloSondeo({ visible: !!visible, fase: 'migrando' });
}

/**
 * ¿Hay que preguntarle al servidor, o basta lo que otra pestaña trajo hace menos de tres cuartos de intervalo? Con dos
 * pestañas abiertas, así pregunta una sola. Un reloj que fue hacia atrás cuenta como viejo.
 */
export function hayQuePreguntar(guardado, ahora, intervalo) {
  var g = normalizarEstado(guardado);
  if (!g || !g.t || !intervalo) return true;
  var edad = ahora - g.t;
  return edad < 0 || edad >= intervalo * 0.75;
}

/**
 * Monta la píldora. op: {urlEstado, emisorId, slot (el <a id="fd-mig-pildora"> del navbar con data-fase, data-porcentaje
 * y data-migracion), ventana (por omisión window), ahora (por omisión Date.now)}. Devuelve {estado(), detener()} o null
 * si falta algo para montarla.
 */
export function iniciarPildora(op) {
  var o = op || {};
  var ventana = o.ventana || (typeof window !== 'undefined' ? window : null);
  var slot = o.slot;
  if (!slot || !ventana || !ventana.document || !o.urlEstado) return null;
  var FD = ventana.FD;
  if (!FD || typeof FD.ajax !== 'function') return null;
  var doc = ventana.document;
  var clave = claveAlmacen(o.emisorId);
  var ahora = typeof o.ahora === 'function' ? o.ahora : function () { return Date.now(); };
  var el = {
    t: slot.querySelector ? slot.querySelector('.fd-mig-pildora-t') : null,
    sep: slot.querySelector ? slot.querySelector('.fd-mig-pildora-sep') : null,
    p: slot.querySelector ? slot.querySelector('.fd-mig-pildora-p') : null,
  };

  var almacen = null;
  try { almacen = ventana.localStorage || null; } catch (e) { almacen = null; }
  function leer() {
    if (!almacen) return null;
    try {
      var s = almacen.getItem(clave);
      return s ? normalizarEstado(JSON.parse(s)) : null;
    } catch (e) { return null; }
  }
  function guardar(s) {
    if (!almacen) return;
    try { almacen.setItem(clave, JSON.stringify(s)); } catch (e) { /* sin espacio o bloqueado: sigue sin compartir */ }
  }
  function borrar() {
    if (!almacen) return;
    try { almacen.removeItem(clave); } catch (e) { /* nada que hacer */ }
  }
  // ¿Se puede leer de verdad? (en navegación privada o con el almacén bloqueado, getItem lanza)
  var conMemoria = false;
  if (almacen) {
    try { almacen.getItem(clave); conMemoria = true; } catch (e) { conMemoria = false; }
  }

  var estado = null;
  var reloj = null;
  var enVuelo = false;
  var detenido = false;        // para siempre: sin permiso, o la corrida ya no se pinta
  var pausaSesion = false;     // 401 / 419: hasta que FD.sesion avise que la sesión volvió
  var dormido = false;         // sin la persona: hasta su siguiente interacción
  var ultimaInteraccion = ahora();

  function pintar(s) {
    var t = textoPildora(s);
    if (!t) return;
    if (el.t) el.t.textContent = t.titulo;
    if (el.p) { el.p.textContent = t.pct; el.p.hidden = !t.pct; }
    if (el.sep) el.sep.hidden = !t.pct;
    slot.setAttribute('aria-label', t.etiqueta);
    slot.setAttribute('title', t.etiqueta);
    slot.setAttribute('data-fase', s.fase);
    slot.setAttribute('data-porcentaje', String(s.porcentaje));
  }

  /** Funde lo nuevo con lo que esta pestaña y las demás ya vieron, avisa si terminó, lo guarda y lo pinta. */
  function aplicar(nuevo) {
    var previo = estado;
    var siguiente = fusionarEstado(fusionarEstado(previo, leer()), nuevo);
    if (!siguiente || !siguiente.fase) return;
    if (debeAvisarListo(previo, siguiente, conMemoria)) {
      siguiente.avisado = true;
      var aviso = avisoListo(siguiente);
      if (typeof FD.toast === 'function') FD.toast(aviso.tipo, aviso.texto);
    }
    estado = siguiente;
    guardar(siguiente);
    pintar(siguiente);
  }

  /** La corrida ya se vio (o el asistente no está): fuera la píldora, sin volver a preguntar. */
  function apagar() {
    detenido = true;
    if (reloj !== null) { ventana.clearTimeout(reloj); reloj = null; }
    borrar();
    var li = slot.closest ? slot.closest('li') : null;
    var nodo = li || slot;
    if (nodo.parentNode) nodo.parentNode.removeChild(nodo);
  }

  function programar() {
    if (reloj !== null) { ventana.clearTimeout(reloj); reloj = null; }
    if (detenido || pausaSesion) return;
    var ms = proximoSondeo(estado, !doc.hidden);
    if (!ms) return;
    if (!sondeoVigente(ultimaInteraccion, ahora())) { dormido = true; return; }
    reloj = ventana.setTimeout(function () { reloj = null; sondear(); }, ms);
  }

  function sondear() {
    if (detenido || pausaSesion || enVuelo) return;
    var ms = proximoSondeo(estado, true);
    if (!ms) return;
    if (!sondeoVigente(ultimaInteraccion, ahora())) { dormido = true; return; }   // venció sin la persona
    var guardado = leer();
    if (!hayQuePreguntar(guardado, ahora(), ms)) {   // otra pestaña acaba de preguntar: basta lo suyo
      aplicar(guardado);
      programar();
      return;
    }
    enVuelo = true;
    FD.ajax({ url: o.urlEstado, data: { pildora: 1 } })
      .done(function (r) {
        if (r && r.success) {
          if (r.pildora) {
            aplicar({ id: r.pildora.migracion_id, porcentaje: r.pildora.porcentaje, fase: r.pildora.fase, pendiente: r.pildora.pendiente, t: ahora() });
          } else {
            apagar();
          }
        } else if (r && r.error === 'NO_DISPONIBLE') {
          apagar();
        }
      })
      .fail(function (x) {
        // La sesión (401) y el token (419) ya los atiende FD.ajax con FD.sesion: se pausa hasta que la sesión vuelva. Sin
        // el permiso (403), se deja de preguntar. Un corte de conexión no detiene nada.
        var st = x ? x.status : 0;
        if (st === 401 || st === 419) pausaSesion = true;
        else if (st === 403) detenido = true;
      })
      .always(function () {
        enVuelo = false;
        programar();
      });
  }

  aplicar({
    id: slot.getAttribute('data-migracion'),
    porcentaje: slot.getAttribute('data-porcentaje'),
    fase: slot.getAttribute('data-fase'),
    pendiente: slot.getAttribute('data-pendiente'),
    t: ahora(),
  });
  programar();

  /** La persona está: cuenta desde ahora y, si los sondeos dormían por su ausencia, pregunta en el acto. */
  function interaccion() {
    ultimaInteraccion = ahora();
    if (!dormido) return;
    dormido = false;
    sondear();
  }

  if (doc.addEventListener) {
    doc.addEventListener('visibilitychange', function () {
      if (doc.hidden) {
        if (reloj !== null) { ventana.clearTimeout(reloj); reloj = null; }
        return;
      }
      ultimaInteraccion = ahora();   // volver a la pestaña es una interacción
      dormido = false;
      sondear();
    });
    doc.addEventListener('pointerdown', interaccion, true);
    doc.addEventListener('keydown', interaccion, true);
    doc.addEventListener('fd:sesion-reanudada', function () {
      if (detenido || !pausaSesion) return;
      pausaSesion = false;
      ultimaInteraccion = ahora();   // la sesión volvió porque la persona entró o eligió seguir
      dormido = false;
      sondear();
    });
  }
  if (ventana.addEventListener) {
    ventana.addEventListener('focus', interaccion);
    ventana.addEventListener('storage', function (ev) {
      if (detenido || !ev || ev.key !== clave) return;
      var otro = leer();
      if (!otro) return;
      estado = fusionarEstado(estado, otro);   // sin volver a escribir: lo escribió la otra pestaña
      pintar(estado);
      programar();
    });
  }

  return {
    estado: function () { return estado; },
    detener: function () {
      detenido = true;
      if (reloj !== null) { ventana.clearTimeout(reloj); reloj = null; }
    },
  };
}
